import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationSkipped, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { filter } from 'rxjs';

import { BreadcrumbModule } from 'primeng/breadcrumb';

@Component({
    selector: 'app-breadcrumbs',
    template: `<p-breadcrumb
        [home]="home"
        [model]="menuItems()"
        [class.empty]="menuItems().length === 0"></p-breadcrumb>`,
    styles: [
        `
            :host ::ng-deep .empty {
                display: none;
            }

            :host ::ng-deep .p-breadcrumb .p-breadcrumb-list li:last-child .p-breadcrumb-item-label {
                text-decoration: none !important;
                cursor: default;
            }

            :host ::ng-deep .p-breadcrumb .p-breadcrumb-list li:last-child .p-breadcrumb-item-link {
                text-decoration: none !important;
                cursor: default;
            }
        `,
    ],
    standalone: true,
    imports: [BreadcrumbModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent implements OnInit {
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private translate = inject(TranslateService);

    static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
    readonly home = { icon: 'fa-regular fa-house', url: environment.quickorderUrl, label: 'Home' };
    menuItems = signal<MenuItem[]>([]);

    ngOnInit(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd || event instanceof NavigationSkipped))
            .subscribe(() => this.menuItems.set(this.createBreadcrumbs(this.activatedRoute.root)));
    }

    private createBreadcrumbs(route: ActivatedRoute, url = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
        const children: ActivatedRoute[] = route.children;

        if (children.length === 0) {
            if (route.snapshot.queryParams['type'] === 'retest') {
                breadcrumbs.push({ label: 'Retest' });
            } else if (route.snapshot.queryParams['type'] === 'investigation') {
                breadcrumbs.push({ label: 'Investigation' });
            }

            return breadcrumbs;
        }

        children.forEach(child => {
            const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');

            if (routeURL !== '') {
                url += `/${routeURL}`;
            }

            const label = child.snapshot.data[BreadcrumbsComponent.ROUTE_DATA_BREADCRUMB];

            if (label !== null && label !== undefined) {
                breadcrumbs.push({ label: this.translate.instant(label), url, target: '_self', routerLink: [url] });
            }

            return this.createBreadcrumbs(child, url, breadcrumbs);
        });

        if (breadcrumbs.length > 0) {
            const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
            delete lastBreadcrumb?.url;
            delete lastBreadcrumb?.routerLink;
            delete lastBreadcrumb?.target;
        }

        return breadcrumbs;
    }
}
